import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import Card from "@mui/material/Card";
import Grid from "@mui/material/Grid";
import SoftBox from "components/SoftBox";
import SoftButton from "components/SoftButton";
import SoftTypography from "components/SoftTypography";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import AccountingStatement5Service from "services/accounting-statement-5";
import { formatVndIntegerValue } from "utils";
import toast from "react-hot-toast";

const ui = {
  title: { fontSize: 20, lineHeight: 1.2 },
  table: { fontSize: 13, borderCollapse: "collapse" },
  th: { border: "1px solid #000", padding: "8px 4px", textAlign: "center", fontSize: 13, fontWeight: "bold", backgroundColor: "#f5f5f5" },
  tdLeft: { border: "1px solid #000", padding: "8px 6px", textAlign: "left", fontSize: 13 },
  tdCenter: { border: "1px solid #000", padding: "8px 4px", textAlign: "center", fontSize: 13 },
  tdRight: { border: "1px solid #000", padding: "8px 6px", textAlign: "right", fontSize: 13, fontFamily: "'Courier New', monospace" },
};

const formatMoney = (value) => formatVndIntegerValue(value || 0);

const buildPrintTemplate = ({ data }) => {
  const { unit, address, month, year, accountCode, debitAccountLabels, rows, totals } = data;
  
  const bodyRows = (rows || [])
    .map(
      (item, idx) => `
      <tr>
        <td class="cell center">${idx + 1}</td>
        <td class="cell center">${item.voucherNumber || ""}</td>
        <td class="cell center">${item.voucherDate || ""}</td>
        <td class="cell left">${item.description || ""}</td>
        ${(item.debitValues || Array(10).fill(0))
          .map(
            (val) =>
              `<td class="cell right">${val ? formatMoney(val) : ""}</td>`
          )
          .join("")}
        <td class="cell right">${item.totalCreditAmount ? formatMoney(item.totalCreditAmount) : ""}</td>
      </tr>`
    )
    .join("");

  return `
  <html>
    <head>
      <meta charset="UTF-8" />
      <title>Bảng kê số 5 (S04-BK5)</title>
      <style>
        body { font-family: 'Times New Roman', serif; color: #000; margin: 24px; font-size: 14px; }
        .header { display: flex; justify-content: space-between; margin-bottom: 12px; }
        .cell, .head { border: 1px solid #000; padding: 6px; vertical-align: middle; }
        .head { font-weight: 700; text-align: center; }
        table { width: 100%; border-collapse: collapse; }
        .title { text-align: center; margin: 10px 0 16px; }
        .title h1 { margin: 0; font-size: 26px; text-transform: uppercase; }
        .title p { margin: 4px 0; }
        .center { text-align: center; }
        .left { text-align: left; }
        .right { text-align: right; }
        .sign { margin-top: 48px; display: flex; justify-content: space-between; align-items: flex-start; text-align: center; }
        .sign-col { width: 32%; }
        .sign-date-row { min-height: 20px; margin-bottom: 2px; }
      </style>
    </head>
    <body>
      <div class="header">
        <div>
          <div><strong>Đơn vị:</strong> ${unit || "......................................"}</div>
          <div><strong>Địa chỉ:</strong> ${address || "......................................"}</div>
        </div>
        <div class="center">
          <div><strong>Mẫu số S04-BK5</strong></div>
          <div><em>(Kèm theo Thông tư số 99/2025/TT-BTC ngày 27 tháng 10 năm 2025 của Bộ trưởng Bộ Tài chính)</em></div>
        </div>
      </div>
      <div class="title">
        <h1>BẢNG KÊ SỐ 5</h1>
        <p>BẢNG KÊ CHI PHÍ SẢN XUẤT, KINH DOANH</p>
        <p>Ghi Có Tài khoản ${accountCode || "...."} - Đối ứng Nợ các TK Chi phí</p>
        <p>Tháng ${month} năm ${year}</p>
      </div>
      <table>
        <thead>
          <tr>
            <th class="head" rowspan="2">Số TT<br/>A</th>
            <th class="head" colspan="2">Chứng từ</th>
            <th class="head" rowspan="2">Diễn giải<br/>D</th>
            <th class="head" colspan="10">Ghi Nợ các Tài khoản đối ứng (Chi phí)</th>
            <th class="head" rowspan="2">Tổng cộng ghi Có TK ${accountCode || "...."}<br/>11</th>
          </tr>
          <tr>
            <th class="head">Số hiệu<br/>B</th>
            <th class="head">Ngày tháng<br/>C</th>
            ${(debitAccountLabels || Array(10).fill(""))
              .map(
                (label, index) =>
                  `<th class="head">${label || "..."}<br/>${index + 1}</th>`
              )
              .join("")}
          </tr>
        </thead>
        <tbody>
          ${bodyRows || '<tr><td class="cell center" colspan="15">Không có dữ liệu</td></tr>'}
          <tr>
            <td class="cell left" colspan="4"><strong>Cộng</strong></td>
            ${(totals?.debitValues || Array(10).fill(0))
              .map(
                (val) =>
                  `<td class="cell right"><strong>${val ? formatMoney(val) : ""}</strong></td>`
              )
              .join("")}
            <td class="cell right"><strong>${formatMoney(totals?.totalCreditAmount || 0)}</strong></td>
          </tr>
        </tbody>
      </table>
      <div class="sign">
        <div class="sign-col">
          <div class="sign-date-row">&nbsp;</div>
          <div><strong>Người ghi sổ</strong></div>
          <div><em>(Ký, họ tên)</em></div>
        </div>
        <div class="sign-col">
          <div class="sign-date-row">&nbsp;</div>
          <div><strong>Kế toán trưởng</strong></div>
          <div><em>(Ký, họ tên)</em></div>
        </div>
        <div class="sign-col">
          <div class="sign-date-row"><em>Ngày ...... tháng ...... năm ......</em></div>
          <div><strong>Người đại diện theo pháp luật</strong></div>
          <div><em>(Ký, họ tên, đóng dấu)</em></div>
        </div>
      </div>
    </body>
  </html>`;
};

export default function AccountingTable5Detail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);

  const fetchData = useCallback(async () => {
    if (!id) return;
    try {
      setLoading(true);
      const response = await AccountingStatement5Service.findById(id);
      setData(response?.data || response || null);
    } catch (error) {
      toast.error(error?.response?.data?.message || "Không thể tải chi tiết Bảng kê số 5");
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handlePrint = () => {
    if (!data) return;
    const printWindow = window.open("", "_blank");
    printWindow.document.write(buildPrintTemplate({ data }));
    printWindow.document.close();
    printWindow.print();
  };

  if (loading) return <SoftBox p={3}>Đang tải...</SoftBox>;
  if (!data) return <SoftBox p={3}>Không tìm thấy dữ liệu</SoftBox>;

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <SoftBox py={3} mb={20}>
        <Grid container justifyContent="center">
          <Grid item xs={12} lg={11}>
            <Card>
              <SoftBox p={3} display="flex" flexDirection="column" minHeight="calc(100vh - 220px)">
                <SoftBox mb={2}>
                  <SoftTypography variant="h5" fontWeight="bold" sx={ui.title}>
                    Bảng kê số 5 (S04-BK5)
                  </SoftTypography>
                </SoftBox>
                <SoftBox border="1px solid #000" p={3} sx={{ fontFamily: "'Times New Roman', serif", color: "#000" }}>
                  <SoftBox display="flex" justifyContent="space-between" mb={2}>
                    <SoftBox>
                      <div style={{ fontSize: 14 }}><strong>Đơn vị:</strong> {data.unit || "......................................"}</div>
                      <div style={{ fontSize: 14 }}><strong>Địa chỉ:</strong> {data.address || "......................................"}</div>
                    </SoftBox>
                    <SoftBox textAlign="center" width="48%">
                      <div style={{ fontSize: 14, fontWeight: 700 }}>Mẫu số S04-BK5</div>
                      <div style={{ fontSize: 14, fontStyle: "italic" }}>(Kèm theo Thông tư số 99/2025/TT-BTC ngày 27 tháng 10 năm 2025 của Bộ trưởng Bộ Tài chính)</div>
                    </SoftBox>
                  </SoftBox>
                  <SoftBox textAlign="center" mb={2}>
                    <div style={{ fontSize: 28, fontWeight: 700, textTransform: "uppercase" }}>BẢNG KÊ SỐ 5</div>
                    <div style={{ fontSize: 18, fontWeight: 700, marginTop: 4 }}>BẢNG KÊ CHI PHÍ SẢN XUẤT, KINH DOANH</div>
                    <div style={{ fontSize: 14, marginTop: 6 }}>Tháng {data.month} năm {data.year}</div>
                    <div style={{ fontSize: 14, marginTop: 4 }}>
                      Ghi Có Tài khoản {data.accountCode || "...."} - Đối ứng Nợ các TK Chi phí
                    </div>
                  </SoftBox>

                  <div style={{ overflowX: "auto" }}>
                    <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 1280, ...ui.table }}>
                      <thead>
                        <tr>
                          <th style={ui.th} rowSpan={2}>Số TT<br />A</th>
                          <th style={ui.th} colSpan={2}>Chứng từ</th>
                          <th style={ui.th} rowSpan={2}>Diễn giải<br />D</th>
                          <th style={ui.th} colSpan={10}>Ghi Nợ các Tài khoản đối ứng (Chi phí)</th>
                          <th style={ui.th} rowSpan={2}>Tổng cộng ghi Có TK {data.accountCode || "...."}<br />11</th>
                        </tr>
                        <tr>
                          <th style={ui.th}>Số hiệu<br />B</th>
                          <th style={ui.th}>Ngày tháng<br />C</th>
                          {(data.debitAccountLabels || Array(10).fill("")).map((label, index) => (
                            <th key={index} style={ui.th}>
                              {label || "..."}<br />{index + 1}
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {data.rows && data.rows.length > 0 ? (
                          data.rows.map((item, idx) => (
                            <tr key={idx}>
                              <td style={ui.tdCenter}>{idx + 1}</td>
                              <td style={ui.tdCenter}>{item.voucherNumber}</td>
                              <td style={ui.tdCenter}>{item.voucherDate}</td>
                              <td style={ui.tdLeft}>{item.description}</td>
                              {(item.debitValues || Array(10).fill(0)).map((val, vIdx) => (
                                <td key={vIdx} style={ui.tdRight}>
                                  {val ? formatMoney(val) : ""}
                                </td>
                              ))}
                              <td style={ui.tdRight}>{item.totalCreditAmount ? formatMoney(item.totalCreditAmount) : ""}</td>
                            </tr>
                          ))
                        ) : (
                          <tr>
                            <td colSpan={15} style={ui.tdCenter}>Không có dữ liệu</td>
                          </tr>
                        )}
                        <tr>
                          <td colSpan={4} style={{ ...ui.tdLeft, fontWeight: "bold" }}>Cộng</td>
                          {(data.totals?.debitValues || Array(10).fill(0)).map((val, idx) => (
                            <td key={idx} style={{ ...ui.tdRight, fontWeight: "bold" }}>
                              {val ? formatMoney(val) : ""}
                            </td>
                          ))}
                          <td style={{ ...ui.tdRight, fontWeight: "bold" }}>{formatMoney(data.totals?.totalCreditAmount)}</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </SoftBox>
                <SoftBox mt="auto" pt={2} display="flex" justifyContent="flex-end" gap={1}>
                  <SoftButton variant="outlined" color="dark" onClick={() => navigate("/templates/accounting-table-5")}>
                    Quay lại
                  </SoftButton>
                  <SoftButton variant="gradient" color="success" onClick={handlePrint}>
                    In Bảng
                  </SoftButton>
                </SoftBox>
              </SoftBox>
            </Card>
          </Grid>
        </Grid>
      </SoftBox>
    </DashboardLayout>
  );
}
